import {
    HttpTransportType, HubConnection, HubConnectionBuilder, LogLevel
} from "@microsoft/signalr";

import { getAccessToken } from "../authentication";
import store from "../store";

/**
 * Helper class for creating and managing SignalR hub connections.
 *
 * This class handles:
 * - Creating SignalR hub connections with Bearer token authentication
 * - Preventing browser tab sleep using the Web Locks API
 * - Automatic reconnection on connection loss
 *
 * The Web Locks API is used to prevent browsers from putting inactive tabs to sleep,
 * which would otherwise cause SignalR connections to drop. Browsers use heuristics
 * (like holding a web lock) to determine if a tab should remain active.
 *
 * @see https://learn.microsoft.com/en-us/aspnet/core/signalr/javascript-client#bsleep
 * @see https://developer.mozilla.org/docs/Web/API/Web_Locks_API
 */
export default class SignalRHubHelper {
    /**
     * Stores the resolver functions for each Web Lock promise, keyed by lock name.
     * Each entry represents an active lock; calling the resolver releases that lock.
     * Using a Map ensures that multiple concurrent hub connections each manage their
     * own lock independently, preventing one connection from releasing another's lock.
     */
    private static lockResolvers = new Map<string, () => void>();

    /**
     * Creates and configures a new SignalR hub connection with optional tab-sleep prevention.
     *
     * The connection is configured with:
     * - Bearer token authentication via MSAL
     * - WebSocket transport with skip negotiation
     * - Automatic reconnection on connection loss
     * - Optional Web Lock to prevent browser tab sleep
     *
     * @param url - The hub endpoint URL (e.g., "/hub/ClientHub")
     * @param logLevel - SignalR logging level (default: Warning)
     * @param preventTabSleep - If true, acquires a Web Lock to prevent tab sleep (default: true)
     * @returns A configured HubConnection ready to be started
     *
     * @example
     * const connection = SignalRHubHelper.createConnection("/hub/ClientHub");
     * await connection.start();
     */
    public static createConnection(
        url: string,
        logLevel: LogLevel = LogLevel.Warning,
        preventTabSleep = true
    ): HubConnection {
        const builder = new HubConnectionBuilder();

        // Acquire Web Lock to prevent browser from putting this tab to sleep
        // This is only attempted if the browser supports the Web Locks API
        if (preventTabSleep) {
            this.acquireWakeLock(url);
        }

        const connection = builder
            // Configure logging for debugging
            .configureLogging(logLevel)
            // Configure WebSocket connection with Bearer token authentication
            .withUrl(store.state.apiBaseUrl + url, {
                accessTokenFactory: async () => (await getAccessToken()) || "",
                transport: HttpTransportType.WebSockets,
                skipNegotiation: true
            })
            // Enable automatic reconnection with default delays (0s, 2s, 10s, 30s)
            .withAutomaticReconnect()
            .build();

        // Release the Web Lock when the connection closes
        // This allows the browser to put the tab to sleep if no other heuristics keep it awake
        connection.onclose(() => {
            this.releaseWakeLock(url);
        });

        return connection;
    }

    /**
     * Acquires a Web Lock to signal to the browser that this tab should remain active.
     *
     * The lock is held indefinitely until releaseWakeLock() is called.
     * While held, the browser considers the tab "active" and won't apply sleep/freezing heuristics.
     *
     * Browser heuristics that trigger tab sleep include:
     * - No user interaction for extended period
     * - Not playing audio
     * - No USB devices connected
     * - Not holding a Web Lock or IndexedDB lock
     *
     * This method silently does nothing if the browser doesn't support Web Locks.
     *
     * @private
     * @see https://developer.mozilla.org/docs/Web/API/Web_Locks_API
     * @see https://learn.microsoft.com/en-us/aspnet/core/signalr/javascript-client?view=aspnetcore-10.0&tabs=visual-studio#bsleep
     */
    private static acquireWakeLock(key: string): void {
        // Check if browser supports Web Locks API
        if (!navigator?.locks?.request) {
            return;
        }

        // Create a promise that will be held for the lifetime of the lock
        // Store the resolver so we can release the lock later
        const promise = new Promise<void>((resolver) => {
            this.lockResolvers.set(key, resolver);
        });

        // Request a shared lock that persists until the promise resolves
        // "shared" mode allows multiple locks with the same name to coexist
        navigator.locks.request(
            `ett-signalr-wake-lock|${key}`,
            { mode: "shared" },
            () => {
                // This callback is invoked when the lock is acquired
                // Return the promise to hold the lock for its lifetime
                return promise;
            }
        ).catch((error) => {
            this.lockResolvers.delete(key);
            // eslint-disable-next-line no-console
            console.warn(
                `Failed to acquire SignalR wake lock for '${key}'.`,
                error
            );
        });
    }

    /**
     * Releases the Web Lock to allow the browser to put the tab to sleep.
     *
     * This is typically called when:
     * - The SignalR connection closes
     * - The user navigates away from the page
     * - The application no longer needs to remain active
     *
     * After the lock is released, the browser's tab-sleep heuristics will apply normally.
     * This method is safe to call even if no lock is currently held for the given key.
     *
     * @private
     */
    private static releaseWakeLock(key: string): void {
        // If a lock resolver exists for this key, calling it will resolve the promise,
        // which releases the Web Lock and allows the tab to sleep
        const resolver = this.lockResolvers.get(key);
        if (resolver) {
            resolver();
            this.lockResolvers.delete(key);
        }
    }
}
